import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Plus } from 'lucide-react';
import { adminGetOrders, describeApiError } from '../../services/api';
import SendInvoiceModal from '../../components/admin/SendInvoiceModal';
import type { Order, OrderStatus } from '../../types';
import { formatCents } from '../../utils/money';
import { formatPhone } from '../../utils/contact';
import { STATUS_LABEL, STATUS_ORDER } from './orderUtils';
import { statusLabel } from '../../utils/orderProgress';

// Whatever contact detail the order actually has. An order taken in person
// often carries a phone number and no email, and showing an empty line
// under the name reads as missing data rather than a different kind of
// order.
const contactLine = (o: Order): string =>
  o.customer_email || (o.customer_phone ? formatPhone(o.customer_phone) : 'No contact details');

const OrdersManagement: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  // Order currently being invoiced from the list, if any.
  const [invoicing, setInvoicing] = useState<Order | undefined>();

  useEffect(() => {
    setLoading(true);
    adminGetOrders(statusFilter || undefined)
      .then(setOrders)
      .catch(e => setError(describeApiError(e, 'Failed to load orders')))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const onInvoiceSent = (updated: Order) => {
    setOrders(prev => prev.map(o => (o.order_id === updated.order_id ? updated : o)));
    setInvoicing(undefined);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
        <h1 className="text-2xl font-semibold">Orders</h1>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-background-dark rounded px-3 py-2 text-sm bg-white"
          >
            <option value="">All statuses</option>
            {STATUS_ORDER.map(s => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
          <Link
            to="/admin/orders/new"
            className="bg-primary text-white rounded px-3 py-2 text-sm font-medium inline-flex items-center gap-1"
          >
            <Plus size={14} /> New order
          </Link>
        </div>
      </div>

      {loading && <div className="text-text-light">Loading…</div>}
      {error && <div className="text-red-600 text-sm">Error: {error}</div>}
      {!loading && !error && orders.length === 0 && (
        <div className="text-text-light text-sm">No orders yet.</div>
      )}

      {orders.length > 0 && (
        <table className="hidden sm:table w-full bg-white rounded border border-background-dark text-sm">
          <thead className="bg-background-dark/50">
            <tr>
              <th className="text-left px-3 py-2">Order #</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Due</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-right px-3 py-2">Invoice</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => {
              // An invoice can only be built from saved line items.
              const canInvoice = o.lines.length > 0;
              return (
                <tr
                  key={o.order_id}
                  onClick={() => navigate(`/admin/orders/${o.order_id}`)}
                  className="border-t border-background-dark cursor-pointer hover:bg-background-dark/30"
                >
                  <td className="px-3 py-2 font-mono text-xs">{o.order_number}</td>
                  <td className="px-3 py-2">
                    <div>{o.customer_name}</div>
                    <div className="text-xs text-text-light">{contactLine(o)}</div>
                  </td>
                  <td className="px-3 py-2">{statusLabel(STATUS_LABEL, o.status as OrderStatus, o.fulfillment)}</td>
                  <td className="px-3 py-2 text-xs">{o.due_date ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{formatCents(o.total_cents)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      // The row navigates; keep the click on the button.
                      onClick={e => {
                        e.stopPropagation();
                        setInvoicing(o);
                      }}
                      disabled={!canInvoice}
                      title={
                        canInvoice
                          ? o.status === 'invoice_sent'
                            ? 'Re-send this invoice'
                            : 'Send an invoice to the customer'
                          : 'Add line items to this order first'
                      }
                      className="inline-flex items-center gap-1 border border-background-dark rounded px-2 py-1 text-xs hover:border-primary disabled:opacity-40 disabled:hover:border-background-dark"
                    >
                      <Mail size={13} />
                      {o.status === 'invoice_sent' ? 'Re-send' : 'Send'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Phone layout. Six columns can't fit a phone, and sideways
          scrolling hides exactly the ones you scan for -- status and
          total. Each order becomes a card instead. */}
      {orders.length > 0 && (
        <ul className="sm:hidden space-y-2">
          {orders.map(o => (
            <li key={o.order_id}>
              <button
                type="button"
                onClick={() => navigate(`/admin/orders/${o.order_id}`)}
                className="w-full text-left bg-white border border-background-dark rounded p-3 active:bg-background-dark/30"
              >
                <div className="flex justify-between gap-3">
                  <span className="font-medium truncate">{o.customer_name}</span>
                  <span className="font-medium shrink-0">{formatCents(o.total_cents)}</span>
                </div>
                <div className="text-xs text-text-light truncate">{contactLine(o)}</div>
                <div className="flex justify-between gap-3 mt-2 text-xs">
                  <span className="font-mono text-text-light">{o.order_number}</span>
                  <span>{statusLabel(STATUS_LABEL, o.status as OrderStatus, o.fulfillment)}</span>
                </div>
                {o.due_date && (
                  <div className="text-xs text-text-light mt-1">Due {o.due_date}</div>
                )}
              </button>
              {o.lines.length > 0 && (
                <button
                  type="button"
                  onClick={() => setInvoicing(o)}
                  className="mt-1 w-full inline-flex items-center justify-center gap-1 border border-background-dark rounded px-2 py-2 text-xs hover:border-primary"
                >
                  <Mail size={13} />
                  {o.status === 'invoice_sent' ? 'Update invoice' : 'Create invoice'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {invoicing && (
        <SendInvoiceModal
          order={invoicing}
          onClose={() => setInvoicing(undefined)}
          onSent={onInvoiceSent}
        />
      )}
    </div>
  );
};

export default OrdersManagement;
